/**
 * HTML Sanitization utilities to prevent XSS attacks.
 * 
 * Use these functions whenever rendering user-generated or API-provided HTML content.
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Removes dangerous tags and attributes while preserving safe formatting.
 * 
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
const SAFE_TAGS = [
  'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
  'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td'
];

export const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SAFE_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'data-atease-signature'],
    ALLOW_DATA_ATTR: false,
    // Force all links to open in new tab with security attributes
    ADD_ATTR: ['target', 'rel'],
  });
};

/**
 * Sanitize email HTML from API, user, or AI content.
 * Inline CSS is intentionally removed because DOMPurify does not validate
 * style values and previews render inside the application origin.
 */
export const sanitizeEmailHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SAFE_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'data-atease-signature'],
    FORBID_ATTR: ['style'],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target', 'rel'],
  });
};

/**
 * Strip all HTML tags and return plain text.
 * Use for displaying content that may contain HTML (e.g. Salesforce feed/comment body).
 *
 * @param {string} html - The HTML string to strip
 * @returns {string} - Plain text with no HTML tags
 */
export const stripHtmlToText = (html) => {
  if (!html) return '';
  const text = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
  return text.trim().replace(/\s+/g, ' ');
};

/**
 * Sanitize and convert newlines to <br/> tags.
 * Use for plain text content that should preserve line breaks.
 *
 * @param {string} text - The text to sanitize and format
 * @returns {string} - Sanitized HTML with line breaks
 */
export const sanitizeWithLineBreaks = (text) => {
  if (!text) return '';
  // First escape HTML entities, then convert newlines
  const escaped = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
  return escaped.replace(/\n/g, '<br/>');
};

/**
 * Create props for dangerouslySetInnerHTML with sanitized content.
 * 
 * @param {string} html - The HTML to sanitize
 * @returns {object} - Object with __html property for dangerouslySetInnerHTML
 */
export const createSanitizedMarkup = (html) => {
  return { __html: sanitizeHtml(html) };
};

export default {
  sanitizeHtml,
  sanitizeEmailHtml,
  stripHtmlToText,
  sanitizeWithLineBreaks,
  createSanitizedMarkup,
};
